import {
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { auth, db } from './firebase';

const normaliseText = (value) => String(value || '').trim();

const normaliseValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value;
  return normaliseText(value);
};

const serialiseDocument = (snapshot) => {
  const data = snapshot.data();
  return {
    id: snapshot.id,
    ...data,
    interactions: Array.isArray(data.interactions) ? data.interactions : [],
    createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt,
    updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : data.updatedAt,
  };
};

export const interactionService = {
  async listInteractions() {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to view interactions.');
    }

    let snapshot;
    try {
      snapshot = await getDocs(query(
        collection(db, 'sharabl-interactions'),
        where('ownerId', '==', auth.currentUser.uid),
      ));
    } catch (error) {
      if (error?.code === 'permission-denied') {
        console.warn('Unable to load Sharabl interactions. Check Firestore rules for sharabl-interactions.', error);
        return [];
      }
      throw error;
    }

    return snapshot.docs.map(serialiseDocument).sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  },

  async submitInteractions(link, grid, interactions = []) {
    if (!link?.id || !grid?.id || !interactions.length) {
      throw new Error('This interaction is missing link, grid, row, or column details.');
    }

    const submittedAt = new Date().toISOString();
    const normalisedInteractions = interactions.map(({ row, column, value }) => {
      if (!row?.id || !column?.id) {
        throw new Error('This interaction is missing row or column details.');
      }

      return {
        id: `interaction-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        gridId: grid.id,
        gridTitle: grid.listTitle || grid.title || '',
        rowId: row.id,
        rowLabel: Object.values(row.cells || {}).find((cellValue) => typeof cellValue === 'string' && cellValue.trim()) || '',
        columnId: column.id,
        columnLabel: column.label || '',
        interactionType: column.interaction?.type || 'text',
        value: normaliseValue(value),
      };
    });

    const payload = {
      linkId: link.id,
      linkSlug: link.slug || '',
      linkTitle: link.title || '',
      ownerId: link.ownerId || '',
      gridId: grid.id,
      gridTitle: grid.listTitle || grid.title || '',
      interactions: normalisedInteractions,
      submittedAt,
    };

    const created = await addDoc(collection(db, 'sharabl-interactions'), {
      ...payload,
      submittedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return { ...payload, id: created.id };
  },

  submitInteraction(link, grid, row, column, value) {
    return this.submitInteractions(link, grid, [{ row, column, value }]);
  },
};
